<template>
  <div>
    <nav>
      <ul class="pager">
        <li class="previous">
          <router-link :to="{ name: 'client_reports', client_id: $route.params.client_id }">
            <span aria-hidden="true">&larr;</span> Reports
          </router-link>
        </li>
      </ul>
    </nav>
    <template v-if="!selected.id">
      <div class="row">
        <div class="col-md-6 col-md-offset-3">
          <h2>Select a Client Farm</h2>

          <div class="panel-body">
            <div class="form-group">
              <v-select label="farm_name" :filterable="true" :clearable="false" :options="farms" v-model="selected">
                <template slot="option" slot-scope="option">
                  {{ currentClient.display_name }} - {{ option.farm_name }}
                </template>
              </v-select>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <h2>
        {{ currentClient.display_name }}
      </h2>
      <h1>
        <template v-if="farmData">
          Animal Wellbeing Plan {{ farmData.season_start_year }}-{{ farmData.season_start_year + 1 }}
        </template>
        <template v-else>
          Animal Wellbeing Plan
        </template>

        {{ selected.farm_name }}
      </h1>
      <h2>
        <a class="btn btn-sm" @click="resetFarm">(Change farm)</a>
      </h2>

      <input type="submit" class="btn btn-primary" value="Generate" @click="fetchFarmData" v-if="!loaded" />

      <template v-if="loaded && hasData">
        <button class="btn btn-default" @click="enableForm" :disabled="!canEdit">
          <i class="glyphicon glyphicon-pencil"></i> Edit
        </button>
        |
        <button class="btn btn-default" @click="discardAndDisableForm" :disabled="!formEnabled">
          <i class="glyphicon glyphicon-remove"></i> Cancel
        </button>
        |
        <button class="btn btn-success" @click="storeData" :disabled="!canSave" :class="{ disabled: !canSave }">
          <i class="glyphicon glyphicon-floppy-save"></i> Save
        </button>
      </template>

      <template v-if="loaded && hasData">
        |
        <a
          :href="exportUrl"
          target="_blank"
          class="btn btn-success"
          :class="{ disabled: !canDownloadPdf }"
          :disabled="!canDownloadPdf"
        >
          <i class="glyphicon glyphicon-download"></i> Download as PDF
        </a>
      </template>

      <div class="alert alert-danger" v-if="showServerError">An error occurred, please contact the administrator.</div>
      <div v-if="loaded">
        <br />
        <table class="table table-striped table-bordered">
          <tbody>
            <tr>
              <td><strong>Farm/Shed Name</strong></td>
              <td>
                <template v-if="formEnabled">
                  <div class="form-group" :class="{ 'has-error': errors.has('farm_name') }">
                    <input type="text" class="form-control" v-model="farmData.farm_name" />
                  </div>
                </template>
                <template v-else>
                  {{ farmData.farm_name }}
                </template>
              </td>
            </tr>
            <tr>
              <td><strong>Vet Name</strong></td>
              <td>
                <template v-if="formEnabled">
                  <select id="vet" name="vet" v-model="farmData.vet" class="form-control">
                    <option :value="vetData.id" v-for="vetData in farmData.vet_options" :key="vetData.id">
                      {{ vetData.name }}
                    </option>
                  </select>
                </template>
                <template v-else>
                  {{ farmData.vet_name }}
                </template>
              </td>
            </tr>
            <tr>
              <td><strong>Vet Clinic</strong></td>
              <td>{{ farmData.clinic_name }}</td>
            </tr>
            <tr>
              <td><strong>Supply Number</strong></td>
              <td>{{ farmData.supply_number }}</td>
            </tr>
            <tr>
              <td><strong>Date</strong></td>
              <td>
                <template v-if="formEnabled">
                  <div class="form-group" :class="{ 'has-error': errors.has('plan_date') }">
                    <input type="date" class="form-control" v-model="farmData.plan_date" />
                  </div>
                  <div v-show="errors.has('plan_date')">
                    <span
                      v-for="(error, index) in errorsFor('plan_date')"
                      class="text-danger"
                      :key="`error_${'plan_date'}_${index}`"
                    >
                      {{ error }}
                    </span>
                  </div>
                </template>
                <template v-else>
                  {{ farmData.plan_date }}
                </template>
              </td>
            </tr>
            <tr>
              <td><strong>Date for review</strong></td>
              <td>
                <template v-if="formEnabled">
                  <div class="form-group" :class="{ 'has-error': errors.has('plan_review_date') }">
                    <input type="date" class="form-control" v-model="farmData.plan_review_date" />
                  </div>
                  <div v-show="errors.has('plan_review_date')">
                    <span
                      v-for="(error, index) in errorsFor('plan_review_date')"
                      class="text-danger"
                      :key="`error_${'plan_review_date'}_${index}`"
                    >
                      {{ error }}
                    </span>
                  </div>
                </template>
                <template v-else>
                  {{ farmData.plan_review_date }}
                </template>
              </td>
            </tr>
          </tbody>
        </table>
        <template v-if="formEnabled">
          <div class="alert alert-danger" v-if="farmErrors">Please review errors below.</div>

          <div class="row metric-form-box">
            <div class="col-xs-12">
              <h4>Mortality Rate Cows</h4>
            </div>
            <div class="col-xs-3">
              <p>
                <strong>Last season's performance</strong>
              </p>
              <div class="input-group" :class="{ 'has-error': errors.has('mortality_cows_performance') }">
                <input
                  type="text"
                  name="mortality_cows_performance"
                  key="mortality_cows_performance"
                  v-validate.immediate="'not_previous_report_data'"
                  @focus="removePreviousText('mortality_cows_performance')"
                  class="form-control"
                  v-model="farmData.mortality_cows_performance"
                />
                <span class="input-group-addon">%</span>
              </div>
              <div v-show="errors.has('mortality_cows_performance')">
                <span
                  v-for="(error, index) in errorsFor('mortality_cows_performance')"
                  class="text-danger"
                  :key="`error_${'mortality_cows_performance'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
            <div class="col-xs-3">
              <p><strong>Industry Target</strong></p>
              {{ farmData.mortality_cows_target }}
            </div>
            <div class="col-xs-3">
              <p><strong>Our Goal</strong></p>
              <div class="input-group" :class="{ 'has-error': errors.has('mortality_cows_goal') }">
                <input
                  type="text"
                  name="mortality_cows_goal"
                  class="form-control"
                  v-model="farmData.mortality_cows_goal"
                  v-validate.immediate="'not_previous_report_data'"
                  key="mortality_cows_goal"
                  @focus="removePreviousText('mortality_cows_goal')"
                />
                <span class="input-group-addon">%</span>
              </div>
              <div v-show="errors.has('mortality_cows_goal')">
                <span
                  v-for="(error, index) in errorsFor('mortality_cows_goal')"
                  class="text-danger"
                  :key="`error_${'mortality_cows_goal'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
            <div class="col-xs-3">
              <p><strong>By when</strong></p>
              <div class="form-group" :class="{ 'has-error': errors.has('mortality_cows_by_when') }">
                <input
                  :type="(farmData.mortality_cows_by_when || '').startsWith('(previous report)') ? 'text' : 'date'"
                  name="mortality_cows_by_when"
                  class="form-control"
                  v-model="farmData.mortality_cows_by_when"
                  v-validate.immediate="'not_previous_report_data'"
                  key="mortality_cows_by_when"
                  @focus="removePreviousText('mortality_cows_by_when')"
                />
              </div>
              <div v-show="errors.has('mortality_cows_by_when')">
                <span
                  v-for="(error, index) in errorsFor('mortality_cows_by_when')"
                  class="text-danger"
                  :key="`error_${'mortality_cows_by_when'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
            <div class="col-xs-12">
              <p><strong>Plan to Achieve Goal</strong></p>
              <div class="form-group" :class="{ 'has-error': errors.has('mortality_cows_plan') }">
                <textarea
                  name="mortality_cows_plan"
                  class="form-control"
                  v-model="farmData.mortality_cows_plan"
                  v-validate.immediate="'not_previous_report_data'"
                  key="mortality_cows_plan"
                  @focus="removePreviousText('mortality_cows_plan')"
                >
                </textarea>
              </div>
              <div v-show="errors.has('mortality_cows_plan')">
                <span
                  v-for="(error, index) in errorsFor('mortality_cows_plan')"
                  class="text-danger"
                  :key="`error_${'mortality_cows_plan'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
          </div>

          <div class="row metric-form-box">
            <div class="col-xs-12">
              <h4>Mortality rate calves (pre weaning)</h4>
            </div>
            <div class="col-xs-3">
              <p>
                <strong>Last season's performance</strong>
              </p>
              <div class="input-group" :class="{ 'has-error': errors.has('mortality_calf_performance') }">
                <input
                  type="text"
                  name="mortality_calf_performance"
                  class="form-control"
                  v-model="farmData.mortality_calf_performance"
                  v-validate.immediate="'not_previous_report_data'"
                  key="mortality_calf_performance"
                  @focus="removePreviousText('mortality_calf_performance')"
                />
                <span class="input-group-addon">%</span>
              </div>
              <div v-show="errors.has('mortality_calf_performance')">
                <span
                  v-for="(error, index) in errorsFor('mortality_calf_performance')"
                  class="text-danger"
                  :key="`error_${'mortality_calf_performance'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
            <div class="col-xs-3">
              <p><strong>Industry Target</strong></p>
              {{ farmData.mortality_calf_target }}
            </div>
            <div class="col-xs-3">
              <p><strong>Our Goal</strong></p>
              <div class="input-group" :class="{ 'has-error': errors.has('mortality_calf_goal') }">
                <input
                  type="text"
                  name="mortality_calf_goal"
                  class="form-control"
                  v-model="farmData.mortality_calf_goal"
                  v-validate.immediate="'not_previous_report_data'"
                  key="mortality_calf_goal"
                  @focus="removePreviousText('mortality_calf_goal')"
                />
                <span class="input-group-addon">%</span>
              </div>
              <div v-show="errors.has('mortality_calf_goal')">
                <span
                  v-for="(error, index) in errorsFor('mortality_calf_goal')"
                  class="text-danger"
                  :key="`error_${'mortality_calf_goal'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
            <div class="col-xs-3">
              <p><strong>By when</strong></p>
              <div class="form-group" :class="{ 'has-error': errors.has('mortality_calf_by_when') }">
                <input
                  :type="(farmData.mortality_calf_by_when || '').startsWith('(previous report)') ? 'text' : 'date'"
                  name="mortality_calf_by_when"
                  class="form-control"
                  v-model="farmData.mortality_calf_by_when"
                  v-validate.immediate="'not_previous_report_data'"
                  key="mortality_calf_by_when"
                  @focus="removePreviousText('mortality_calf_by_when')"
                />
              </div>
              <div v-show="errors.has('mortality_calf_by_when')">
                <span
                  v-for="(error, index) in errorsFor('mortality_calf_by_when')"
                  class="text-danger"
                  :key="`error_${'mortality_calf_by_when'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
            <div class="col-xs-12">
              <p><strong>Plan to Achieve Goal</strong></p>
              <div class="form-group" :class="{ 'has-error': errors.has('mortality_calf_plan') }">
                <textarea
                  name="mortality_calf_plan"
                  class="form-control"
                  v-model="farmData.mortality_calf_plan"
                  v-validate.immediate="'not_previous_report_data'"
                  key="mortality_calf_plan"
                  @focus="removePreviousText('mortality_calf_plan')"
                >
                </textarea>
              </div>
              <div v-show="errors.has('mortality_calf_plan')">
                <span
                  v-for="(error, index) in errorsFor('mortality_calf_plan')"
                  class="text-danger"
                  :key="`error_${'mortality_calf_plan'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
          </div>

          <div class="row metric-form-box">
            <div class="col-xs-12">
              <h4>BCS</h4>
            </div>
            <div class="col-xs-3">
              <p>
                <strong>Last season's performance</strong>
              </p>
              <div class="form-group" :class="{ 'has-error': errors.has('bcs_performance') }">
                <input
                  type="text"
                  name="bcs_performance"
                  class="form-control"
                  v-model="farmData.bcs_performance"
                  v-validate.immediate="'not_previous_report_data'"
                  key="bcs_performance"
                  @focus="removePreviousText('bcs_performance')"
                />
              </div>
              <div v-show="errors.has('bcs_performance')">
                <span
                  v-for="(error, index) in errorsFor('bcs_performance')"
                  class="text-danger"
                  :key="`error_${'bcs_performance'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
            <div class="col-xs-3">
              <p><strong>Industry Target</strong></p>
              {{ farmData.bcs_target }}
            </div>
            <div class="col-xs-3">
              <p><strong>Our Goal</strong></p>
              <div class="form-group" :class="{ 'has-error': errors.has('bcs_goal') }">
                <input
                  type="text"
                  name="bcs_goal"
                  class="form-control"
                  v-model="farmData.bcs_goal"
                  v-validate.immediate="'not_previous_report_data'"
                  key="bcs_goal"
                  @focus="removePreviousText('bcs_goal')"
                />
              </div>
              <div v-show="errors.has('bcs_goal')">
                <span
                  v-for="(error, index) in errorsFor('bcs_goal')"
                  class="text-danger"
                  :key="`error_${'bcs_goal'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
            <div class="col-xs-3">
              <p><strong>By when</strong></p>
              <div class="form-group" :class="{ 'has-error': errors.has('bcs_by_when') }">
                <input
                  :type="(farmData.bcs_by_when || '').startsWith('(previous report)') ? 'text' : 'date'"
                  name="bcs_by_when"
                  class="form-control"
                  v-model="farmData.bcs_by_when"
                  v-validate.immediate="'not_previous_report_data'"
                  key="bcs_by_when"
                  @focus="removePreviousText('bcs_by_when')"
                />
              </div>
              <div v-show="errors.has('bcs_by_when')">
                <span
                  v-for="(error, index) in errorsFor('bcs_by_when')"
                  class="text-danger"
                  :key="`error_${'bcs_by_when'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
            <div class="col-xs-12">
              <p><strong>Plan to Achieve Goal</strong></p>
              <div class="form-group" :class="{ 'has-error': errors.has('bcs_plan') }">
                <textarea
                  name="bcs_plan"
                  class="form-control"
                  v-model="farmData.bcs_plan"
                  v-validate.immediate="'not_previous_report_data'"
                  key="bcs_plan"
                  @focus="removePreviousText('bcs_plan')"
                >
                </textarea>
              </div>
              <div v-show="errors.has('bcs_plan')">
                <span
                  v-for="(error, index) in errorsFor('bcs_plan')"
                  class="text-danger"
                  :key="`error_${'bcs_plan'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
          </div>

          <div class="row metric-form-box">
            <div class="col-xs-12">
              <h4>Cows with BCS &lt; 3.5</h4>
            </div>
            <div class="col-xs-3">
              <p>
                <strong>Last season's performance</strong>
              </p>
              <div class="input-group" :class="{ 'has-error': errors.has('bcs_lt35_performance') }">
                <input
                  type="text"
                  name="bcs_lt35_performance"
                  class="form-control"
                  v-model="farmData.bcs_lt35_performance"
                  v-validate.immediate="'not_previous_report_data'"
                  key="bcs_lt35_performance"
                  @focus="removePreviousText('bcs_lt35_performance')"
                />
                <span class="input-group-addon">%</span>
              </div>
              <div v-show="errors.has('bcs_lt35_performance')">
                <span
                  v-for="(error, index) in errorsFor('bcs_lt35_performance')"
                  class="text-danger"
                  :key="`error_${'bcs_lt35_performance'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
            <div class="col-xs-3">
              <p><strong>Industry Target</strong></p>
              {{ farmData.bcs_lt35_target }}
            </div>
            <div class="col-xs-3">
              <p><strong>Our Goal</strong></p>
              <div class="input-group" :class="{ 'has-error': errors.has('bcs_lt35_goal') }">
                <input
                  type="text"
                  name="bcs_lt35_goal"
                  class="form-control"
                  v-model="farmData.bcs_lt35_goal"
                  v-validate.immediate="'not_previous_report_data'"
                  key="bcs_lt35_goal"
                  @focus="removePreviousText('bcs_lt35_goal')"
                />
                <span class="input-group-addon">%</span>
              </div>
              <div v-show="errors.has('bcs_lt35_goal')">
                <span
                  v-for="(error, index) in errorsFor('bcs_lt35_goal')"
                  class="text-danger"
                  :key="`error_${'bcs_lt35_goal'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
            <div class="col-xs-3">
              <p><strong>By when</strong></p>
              <div class="form-group" :class="{ 'has-error': errors.has('bcs_lt35_by_when') }">
                <input
                  :type="(farmData.bcs_lt35_by_when || '').startsWith('(previous report)') ? 'text' : 'date'"
                  name="bcs_lt35_by_when"
                  class="form-control"
                  v-model="farmData.bcs_lt35_by_when"
                  v-validate.immediate="'not_previous_report_data'"
                  key="bcs_lt35_by_when"
                  @focus="removePreviousText('bcs_lt35_by_when')"
                />
              </div>
              <div v-show="errors.has('bcs_lt35_by_when')">
                <span
                  v-for="(error, index) in errorsFor('bcs_lt35_by_when')"
                  class="text-danger"
                  :key="`error_${'bcs_lt35_by_when'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
            <div class="col-xs-12">
              <p><strong>Plan to Achieve Goal</strong></p>
              <div class="form-group" :class="{ 'has-error': errors.has('bcs_lt35_plan') }">
                <textarea
                  name="bcs_lt35_plan"
                  class="form-control"
                  v-model="farmData.bcs_lt35_plan"
                  v-validate.immediate="'not_previous_report_data'"
                  key="bcs_lt35_plan"
                  @focus="removePreviousText('bcs_lt35_plan')"
                >
                </textarea>
              </div>
              <div v-show="errors.has('bcs_lt35_plan')">
                <span
                  v-for="(error, index) in errorsFor('bcs_lt35_plan')"
                  class="text-danger"
                  :key="`error_${'bcs_lt35_plan'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
          </div>

          <div class="row metric-form-box">
            <div class="col-xs-12">
              <h4>Clinical mastitis rate</h4>
            </div>
            <div class="col-xs-3">
              <p>
                <strong>Last season's performance</strong>
              </p>
              <div class="input-group" :class="{ 'has-error': errors.has('mastitis_performance') }">
                <input
                  type="text"
                  name="mastitis_performance"
                  class="form-control"
                  v-model="farmData.mastitis_performance"
                  v-validate.immediate="'not_previous_report_data'"
                  key="mastitis_performance"
                  @focus="removePreviousText('mastitis_performance')"
                />
                <span class="input-group-addon">%</span>
              </div>
              <div v-show="errors.has('mastitis_performance')">
                <span
                  v-for="(error, index) in errorsFor('mastitis_performance')"
                  class="text-danger"
                  :key="`error_${'mastitis_performance'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
            <div class="col-xs-3">
              <p><strong>Industry Target</strong></p>
              {{ farmData.mastitis_target }}
            </div>
            <div class="col-xs-3">
              <p><strong>Our Goal</strong></p>
              <div class="input-group" :class="{ 'has-error': errors.has('mastitis_goal') }">
                <input
                  type="text"
                  name="mastitis_goal"
                  class="form-control"
                  v-model="farmData.mastitis_goal"
                  v-validate.immediate="'not_previous_report_data'"
                  key="mastitis_goal"
                  @focus="removePreviousText('mastitis_goal')"
                />
                <span class="input-group-addon">%</span>
              </div>
              <div v-show="errors.has('mastitis_goal')">
                <span
                  v-for="(error, index) in errorsFor('mastitis_goal')"
                  class="text-danger"
                  :key="`error_${'mastitis_goal'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
            <div class="col-xs-3">
              <p><strong>By when</strong></p>
              <div class="form-group" :class="{ 'has-error': errors.has('mastitis_by_when') }">
                <input
                  :type="(farmData.mastitis_by_when || '').startsWith('(previous report)') ? 'text' : 'date'"
                  name="mastitis_by_when"
                  class="form-control"
                  v-model="farmData.mastitis_by_when"
                  v-validate.immediate="'not_previous_report_data'"
                  key="mastitis_by_when"
                  @focus="removePreviousText('mastitis_by_when')"
                />
              </div>
              <div v-show="errors.has('mastitis_by_when')">
                <span
                  v-for="(error, index) in errorsFor('mastitis_by_when')"
                  class="text-danger"
                  :key="`error_${'mastitis_by_when'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
            <div class="col-xs-12">
              <p><strong>Plan to Achieve Goal</strong></p>
              <div class="form-group" :class="{ 'has-error': errors.has('mastitis_plan') }">
                <textarea
                  name="mastitis_plan"
                  class="form-control"
                  v-model="farmData.mastitis_plan"
                  v-validate.immediate="'not_previous_report_data'"
                  key="mastitis_plan"
                  @focus="removePreviousText('mastitis_plan')"
                >
                </textarea>
              </div>
              <div v-show="errors.has('mastitis_plan')">
                <span
                  v-for="(error, index) in errorsFor('mastitis_plan')"
                  class="text-danger"
                  :key="`error_${'mastitis_plan'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
          </div>

          <div class="row metric-form-box">
            <div class="col-xs-12">
              <h4>Lameness incidence</h4>
            </div>
            <div class="col-xs-3">
              <p>
                <strong>Last season's performance</strong>
              </p>
              <div class="input-group" :class="{ 'has-error': errors.has('lameness_performance') }">
                <input
                  type="text"
                  name="lameness_performance"
                  class="form-control"
                  v-model="farmData.lameness_performance"
                  v-validate.immediate="'not_previous_report_data'"
                  key="lameness_performance"
                  @focus="removePreviousText('lameness_performance')"
                />
                <span class="input-group-addon">%</span>
              </div>
              <div v-show="errors.has('lameness_performance')">
                <span
                  v-for="(error, index) in errorsFor('lameness_performance')"
                  class="text-danger"
                  :key="`error_${'lameness_performance'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
            <div class="col-xs-3">
              <p><strong>Industry Target</strong></p>
              {{ farmData.lameness_target }}
            </div>
            <div class="col-xs-3">
              <p><strong>Our Goal</strong></p>
              <div class="input-group" :class="{ 'has-error': errors.has('lameness_goal') }">
                <input
                  type="text"
                  name="lameness_goal"
                  class="form-control"
                  v-model="farmData.lameness_goal"
                  v-validate.immediate="'not_previous_report_data'"
                  key="lameness_goal"
                  @focus="removePreviousText('lameness_goal')"
                />
                <span class="input-group-addon">%</span>
              </div>
              <div v-show="errors.has('lameness_goal')">
                <span
                  v-for="(error, index) in errorsFor('lameness_goal')"
                  class="text-danger"
                  :key="`error_${'lameness_goal'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
            <div class="col-xs-3">
              <p><strong>By when</strong></p>
              <div class="form-group" :class="{ 'has-error': errors.has('lameness_by_when') }">
                <input
                  :type="(farmData.lameness_by_when || '').startsWith('(previous report)') ? 'text' : 'date'"
                  name="lameness_by_when"
                  class="form-control"
                  v-model="farmData.lameness_by_when"
                  v-validate.immediate="'not_previous_report_data'"
                  key="lameness_by_when"
                  @focus="removePreviousText('lameness_by_when')"
                />
              </div>
              <div v-show="errors.has('lameness_by_when')">
                <span
                  v-for="(error, index) in errorsFor('lameness_by_when')"
                  class="text-danger"
                  :key="`error_${'lameness_by_when'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
            <div class="col-xs-12">
              <p><strong>Plan to Achieve Goal</strong></p>
              <div class="form-group" :class="{ 'has-error': errors.has('lameness_plan') }">
                <textarea
                  name="lameness_plan"
                  class="form-control"
                  v-model="farmData.lameness_plan"
                  v-validate.immediate="'not_previous_report_data'"
                  key="lameness_plan"
                  @focus="removePreviousText('lameness_plan')"
                >
                </textarea>
              </div>
              <div v-show="errors.has('lameness_plan')">
                <span
                  v-for="(error, index) in errorsFor('lameness_plan')"
                  class="text-danger"
                  :key="`error_${'lameness_plan'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
          </div>

          <div class="row metric-form-box">
            <div class="col-xs-12">
              <h4>AM usage (Annual PCU)</h4>
            </div>
            <div class="col-xs-3">
              <p>
                <strong>Last season's performance</strong>
              </p>
              <div class="input-group" :class="{ 'has-error': errors.has('amu_performance') }">
                <input
                  type="text"
                  name="amu_performance"
                  class="form-control"
                  v-model="farmData.amu_performance"
                  v-validate.immediate="'not_previous_report_data'"
                  key="amu_performance"
                  @focus="removePreviousText('amu_performance')"
                />
                <span class="input-group-addon">mg/kg</span>
              </div>
              <div v-show="errors.has('amu_performance')">
                <span
                  v-for="(error, index) in errorsFor('amu_performance')"
                  class="text-danger"
                  :key="`error_${'amu_performance'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
            <div class="col-xs-3">
              <p><strong>Industry Target</strong></p>
              {{ farmData.amu_target }}
            </div>
            <div class="col-xs-3">
              <p><strong>Our Goal</strong></p>
              <div class="input-group" :class="{ 'has-error': errors.has('amu_goal') }">
                <input
                  type="text"
                  name="amu_goal"
                  class="form-control"
                  v-model="farmData.amu_goal"
                  v-validate.immediate="'not_previous_report_data'"
                  key="amu_goal"
                  @focus="removePreviousText('amu_goal')"
                />
                <span class="input-group-addon">mg/kg</span>
              </div>
              <div v-show="errors.has('amu_goal')">
                <span
                  v-for="(error, index) in errorsFor('amu_goal')"
                  class="text-danger"
                  :key="`error_${'amu_goal'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
            <div class="col-xs-3">
              <p><strong>By when</strong></p>
              <div class="form-group" :class="{ 'has-error': errors.has('amu_by_when') }">
                <input
                  :type="(farmData.amu_by_when || '').startsWith('(previous report)') ? 'text' : 'date'"
                  name="amu_by_when"
                  class="form-control"
                  v-model="farmData.amu_by_when"
                  v-validate.immediate="'not_previous_report_data'"
                  key="amu_by_when"
                  @focus="removePreviousText('amu_by_when')"
                />
              </div>
              <div v-show="errors.has('amu_by_when')">
                <span
                  v-for="(error, index) in errorsFor('amu_by_when')"
                  class="text-danger"
                  :key="`error_${'amu_by_when'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
            <div class="col-xs-12">
              <p><strong>Plan to Achieve Goal</strong></p>
              <div class="form-group" :class="{ 'has-error': errors.has('amu_plan') }">
                <textarea
                  name="amu_plan"
                  class="form-control"
                  v-model="farmData.amu_plan"
                  v-validate.immediate="'not_previous_report_data'"
                  key="amu_plan"
                  @focus="removePreviousText('amu_plan')"
                >
                </textarea>
              </div>
              <div v-show="errors.has('amu_plan')">
                <span
                  v-for="(error, index) in errorsFor('amu_plan')"
                  class="text-danger"
                  :key="`error_${'amu_plan'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
          </div>

          <div class="row metric-form-box">
            <div class="row">
              <div class="col-xs-12">
                <h4>Reproduction</h4>
              </div>
            </div>
            <div class="row">
              <div class="col-xs-12">
                <h4>3 week ICR</h4>
              </div>
              <div class="col-xs-3">
                <p>
                  <strong>Last season's performance</strong>
                </p>
                <div class="input-group" :class="{ 'has-error': errors.has('three_weeks_performance') }">
                  <input
                    type="text"
                    name="three_weeks_performance"
                    class="form-control"
                    v-model="farmData.three_weeks_performance"
                    v-validate.immediate="'not_previous_report_data'"
                    key="three_weeks_performance"
                    @focus="removePreviousText('three_weeks_performance')"
                  />
                  <span class="input-group-addon">%</span>
                </div>
                <div v-show="errors.has('three_week_performance')">
                  <span
                    v-for="(error, index) in errorsFor('three_week_performance')"
                    class="text-danger"
                    :key="`error_${'three_week_performance'}_${index}`"
                  >
                    {{ error }}
                  </span>
                </div>
              </div>
              <div class="col-xs-3">
                <p><strong>Industry Target</strong></p>
                {{ farmData.three_weeks_target }}
              </div>
              <div class="col-xs-3">
                <p><strong>Our Goal</strong></p>
                <div class="input-group" :class="{ 'has-error': errors.has('three_weeks_goal') }">
                  <input
                    type="text"
                    name="three_weeks_goal"
                    class="form-control"
                    v-model="farmData.three_weeks_goal"
                    v-validate.immediate="'not_previous_report_data'"
                    key="three_weeks_goal"
                    @focus="removePreviousText('three_weeks_goal')"
                  />
                  <span class="input-group-addon">%</span>
                </div>
                <div v-show="errors.has('three_weeks_goal')">
                  <span
                    v-for="(error, index) in errorsFor('three_weeks_goal')"
                    class="text-danger"
                    :key="`error_${'three_weeks_goal'}_${index}`"
                  >
                    {{ error }}
                  </span>
                </div>
              </div>
              <div class="col-xs-3">
                <p><strong>By when</strong></p>
                <div class="form-group" :class="{ 'has-error': errors.has('three_weeks_by_when') }">
                  <input
                    :type="(farmData.three_weeks_by_when || '').startsWith('(previous report)') ? 'text' : 'date'"
                    name="three_weeks_by_when"
                    class="form-control"
                    v-model="farmData.three_weeks_by_when"
                    v-validate.immediate="'not_previous_report_data'"
                    key="three_weeks_by_when"
                    @focus="removePreviousText('three_weeks_by_when')"
                  />
                </div>
                <div v-show="errors.has('three_weeks_by_when')">
                  <span
                    v-for="(error, index) in errorsFor('three_weeks_by_when')"
                    class="text-danger"
                    :key="`error_${'three_weeks_by_when'}_${index}`"
                  >
                    {{ error }}
                  </span>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-xs-12">
                <h4>6 week ICR</h4>
              </div>
              <div class="col-xs-3">
                <p>
                  <strong>Last season's performance</strong>
                </p>
                <div class="input-group" :class="{ 'has-error': errors.has('six_weeks_performance') }">
                  <input
                    type="text"
                    name="six_weeks_performance"
                    class="form-control"
                    v-model="farmData.six_weeks_performance"
                    v-validate.immediate="'not_previous_report_data'"
                    key="six_weeks_performance"
                    @focus="removePreviousText('six_weeks_performance')"
                  />
                  <span class="input-group-addon">%</span>
                </div>
                <div v-show="errors.has('six_week_performance')">
                  <span
                    v-for="(error, index) in errorsFor('six_week_performance')"
                    class="text-danger"
                    :key="`error_${'six_week_performance'}_${index}`"
                  >
                    {{ error }}
                  </span>
                </div>
              </div>
              <div class="col-xs-3">
                <p><strong>Industry Target</strong></p>
                {{ farmData.six_weeks_target }}
              </div>
              <div class="col-xs-3">
                <p><strong>Our Goal</strong></p>
                <div class="input-group" :class="{ 'has-error': errors.has('six_weeks_goal') }">
                  <input
                    type="text"
                    name="six_weeks_goal"
                    class="form-control"
                    v-model="farmData.six_weeks_goal"
                    v-validate.immediate="'not_previous_report_data'"
                    key="six_weeks_goal"
                    @focus="removePreviousText('six_weeks_goal')"
                  />
                  <span class="input-group-addon">%</span>
                </div>
                <div v-show="errors.has('six_weeks_goal')">
                  <span
                    v-for="(error, index) in errorsFor('six_weeks_goal')"
                    class="text-danger"
                    :key="`error_${'six_weeks_goal'}_${index}`"
                  >
                    {{ error }}
                  </span>
                </div>
              </div>
              <div class="col-xs-3">
                <p><strong>By when</strong></p>
                <div class="form-group" :class="{ 'has-error': errors.has('six_weeks_by_when') }">
                  <input
                    :type="(farmData.six_weeks_by_when || '').startsWith('(previous report)') ? 'text' : 'date'"
                    name="six_weeks_by_when"
                    class="form-control"
                    v-model="farmData.six_weeks_by_when"
                    v-validate.immediate="'not_previous_report_data'"
                    key="six_weeks_by_when"
                    @focus="removePreviousText('six_weeks_by_when')"
                  />
                </div>
                <div v-show="errors.has('six_weeks_by_when')">
                  <span
                    v-for="(error, index) in errorsFor('six_weeks_by_when')"
                    class="text-danger"
                    :key="`error_${'six_weeks_by_when'}_${index}`"
                  >
                    {{ error }}
                  </span>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-xs-12">
                <h4>NICR</h4>
              </div>
              <div class="col-xs-3">
                <p>
                  <strong>Last season's performance</strong>
                </p>
                <div class="input-group" :class="{ 'has-error': errors.has('nine_weeks_performance') }">
                  <input
                    type="text"
                    name="nine_weeks_performance"
                    class="form-control"
                    v-model="farmData.nine_weeks_performance"
                    v-validate.immediate="'not_previous_report_data'"
                    key="nine_weeks_performance"
                    @focus="removePreviousText('nine_weeks_performance')"
                  />
                  <span class="input-group-addon">%</span>
                </div>
                <div v-show="errors.has('nine_week_performance')">
                  <span
                    v-for="(error, index) in errorsFor('nine_week_performance')"
                    class="text-danger"
                    :key="`error_${'nine_week_performance'}_${index}`"
                  >
                    {{ error }}
                  </span>
                </div>
              </div>
              <div class="col-xs-3">
                <p><strong>Industry Target</strong></p>
                {{ farmData.nine_weeks_target }}
              </div>
              <div class="col-xs-3">
                <p><strong>Our Goal</strong></p>
                <div class="input-group" :class="{ 'has-error': errors.has('nine_weeks_goal') }">
                  <input
                    type="text"
                    name="nine_weeks_goal"
                    class="form-control"
                    v-model="farmData.nine_weeks_goal"
                    v-validate.immediate="'not_previous_report_data'"
                    key="nine_weeks_goal"
                    @focus="removePreviousText('nine_weeks_goal')"
                  />
                  <span class="input-group-addon">%</span>
                </div>
                <div v-show="errors.has('nine_weeks_goal')">
                  <span
                    v-for="(error, index) in errorsFor('nine_weeks_goal')"
                    class="text-danger"
                    :key="`error_${'nine_weeks_goal'}_${index}`"
                  >
                    {{ error }}
                  </span>
                </div>
              </div>
              <div class="col-xs-3">
                <p><strong>By when</strong></p>
                <div class="form-group" :class="{ 'has-error': errors.has('nine_weeks_by_when') }">
                  <input
                    :type="(farmData.nine_weeks_by_when || '').startsWith('(previous report)') ? 'text' : 'date'"
                    name="nine_weeks_by_when"
                    class="form-control"
                    v-model="farmData.nine_weeks_by_when"
                    v-validate.immediate="'not_previous_report_data'"
                    key="nine_weeks_by_when"
                    @focus="removePreviousText('nine_weeks_by_when')"
                  />
                </div>
                <div v-show="errors.has('nine_weeks_by_when')">
                  <span
                    v-for="(error, index) in errorsFor('nine_weeks_by_when')"
                    class="text-danger"
                    :key="`error_${'nine_weeks_by_when'}_${index}`"
                  >
                    {{ error }}
                  </span>
                </div>
              </div>
            </div>
            <div class="row">

              <div class="col-xs-12">
                <p><strong>Plan to Achieve Goal</strong></p>
                <div class="form-group" :class="{ 'has-error': errors.has('icr_plan') }">
                  <textarea
                    name="icr_plan"
                    class="form-control"
                    v-model="farmData.icr_plan"
                    v-validate.immediate="'not_previous_report_data'"
                    key="icr_plan"
                    @focus="removePreviousText('icr_plan')"
                  >
                  </textarea>
                </div>
                <div v-show="errors.has('icr_plan')">
                  <span
                    v-for="(error, index) in errorsFor('icr_plan')"
                    class="text-danger"
                    :key="`error_${'icr_plan'}_${index}`"
                  >
                    {{ error }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="row metric-form-box">
            <div class="col-xs-12">
              <h4>Calf Plans</h4>
            </div>
            <div class="col-xs-3">
              <strong>Calf Nutrition Plan</strong>
            </div>
            <div class="col-xs-9">
              <div class="form-group" :class="{ 'has-error': errors.has('calf_nutrition_plan') }">
                <textarea
                  name="calf_nutrition_plan"
                  class="form-control"
                  v-model="farmData.calf_nutrition_plan"
                  v-validate.immediate="'not_previous_report_data'"
                  key="calf_nutrition_plan"
                  @focus="removePreviousText('calf_nutrition_plan')"
                >
                </textarea>
              </div>
              <div v-show="errors.has('calf_nutrition_plan')">
                <span
                  v-for="(error, index) in errorsFor('calf_nutrition_plan')"
                  class="text-danger"
                  :key="`error_${'calf_nutrition_plan'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
            <div class="col-xs-3">
              <strong>Calf Health Plan</strong>
            </div>
            <div class="col-xs-9">
              <div class="form-group" :class="{ 'has-error': errors.has('calf_health_plan') }">
                <textarea
                  name="calf_health_plan"
                  class="form-control"
                  v-model="farmData.calf_health_plan"
                  v-validate.immediate="'not_previous_report_data'"
                  key="calf_health_plan"
                  @focus="removePreviousText('calf_health_plan')"
                >
                </textarea>
              </div>
              <div v-show="errors.has('calf_health_plan')">
                <span
                  v-for="(error, index) in errorsFor('calf_health_plan')"
                  class="text-danger"
                  :key="`error_${'calf_health_plan'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
            <div class="col-xs-3">
              <strong>Calf Housing Plan</strong>
            </div>
            <div class="col-xs-9">
              <div class="form-group" :class="{ 'has-error': errors.has('calf_housing_plan') }">
                <textarea
                  name="calf_housing_plan"
                  class="form-control"
                  v-model="farmData.calf_housing_plan"
                  v-validate.immediate="'not_previous_report_data'"
                  key="calf_housing_plan"
                  @focus="removePreviousText('calf_housing_plan')"
                >
                </textarea>
              </div>
              <div v-show="errors.has('calf_housing_plan')">
                <span
                  v-for="(error, index) in errorsFor('calf_housing_plan')"
                  class="text-danger"
                  :key="`error_${'calf_housing_plan'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>

            <div class="col-xs-3">
              <strong> Youngstock Plan - weaning to pregnancy Plan</strong>
            </div>
            <div class="col-xs-9">
              <div class="form-group" :class="{ 'has-error': errors.has('weaning_plan') }">
                <textarea
                  name="weaning_plan"
                  class="form-control"
                  v-model="farmData.weaning_plan"
                  v-validate.immediate="'not_previous_report_data'"
                  key="weaning_plan"
                  @focus="removePreviousText('weaning_plan')"
                >
                </textarea>
              </div>
              <div v-show="errors.has('weaning_plan')">
                <span
                  v-for="(error, index) in errorsFor('weaning_plan')"
                  class="text-danger"
                  :key="`error_${'weaning_plan'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
          </div>
          <div class="row metric-form-box">
            <div class="col-xs-12">
              <h4>Farm Plans</h4>
            </div>
            <div class="col-xs-3">
              <strong>Adverse Weather Plan</strong>
            </div>
            <div class="col-xs-9">
              <div class="form-group" :class="{ 'has-error': errors.has('adverse_weather_plan') }">
                <textarea
                  name="adverse_weather_plan"
                  class="form-control"
                  v-model="farmData.adverse_weather_plan"
                  v-validate.immediate="'not_previous_report_data'"
                  key="adverse_weather_plan"
                  @focus="removePreviousText('adverse_weather_plan')"
                >
                </textarea>
              </div>
              <div v-show="errors.has('adverse_weather_plan')">
                <span
                  v-for="(error, index) in errorsFor('adverse_weather_plan')"
                  class="text-danger"
                  :key="`error_${'adverse_weather_plan'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
            <div class="col-xs-3">
              <strong>Heat Stress Plan</strong>
            </div>
            <div class="col-xs-9">
              <div class="form-group" :class="{ 'has-error': errors.has('heat_stress_plan') }">
                <textarea
                  name="heat_stress_plan"
                  class="form-control"
                  v-model="farmData.heat_stress_plan"
                  v-validate.immediate="'not_previous_report_data'"
                  key="heat_stress_plan"
                  @focus="removePreviousText('heat_stress_plan')"
                >
                </textarea>
              </div>
              <div v-show="errors.has('heat_stress_plan')">
                <span
                  v-for="(error, index) in errorsFor('heat_stress_plan')"
                  class="text-danger"
                  :key="`error_${'heat_stress_plan'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
            <div class="col-xs-3">
              <strong>Genetic Improvement (AW) Plan</strong>
            </div>
            <div class="col-xs-9">
              <div class="form-group" :class="{ 'has-error': errors.has('genetic_improvement_plan') }">
                <textarea
                  name="genetic_improvement_plan"
                  class="form-control"
                  v-model="farmData.genetic_improvement_plan"
                  v-validate.immediate="'not_previous_report_data'"
                  key="genetic_improvement_plan"
                  @focus="removePreviousText('genetic_improvement_plan')"
                >
                </textarea>
              </div>
              <div v-show="errors.has('genetic_improvement_plan')">
                <span
                  v-for="(error, index) in errorsFor('genetic_improvement_plan')"
                  class="text-danger"
                  :key="`error_${'genetic_improvement_plan'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>

            <div class="col-xs-3">
              <strong>Off-Pasture Grazing Facility Use Plan</strong>
            </div>
            <div class="col-xs-9">
              <div class="form-group" :class="{ 'has-error': errors.has('off_pasture_grazing_facility_use_plan') }">
                <textarea
                  name="off_pasture_grazing_facility_use_plan"
                  class="form-control"
                  v-model="farmData.off_pasture_grazing_facility_use_plan"
                  v-validate.immediate="'not_previous_report_data'"
                  key="off_pasture_grazing_facility_use_plan"
                  @focus="removePreviousText('off_pasture_grazing_facility_use_plan')"
                >
                </textarea>
              </div>
              <div v-show="errors.has('off_pasture_grazing_facility_use_plan')">
                <span
                  v-for="(error, index) in errorsFor('off_pasture_grazing_facility_use_plan')"
                  class="text-danger"
                  :key="`error_${'off_pasture_grazing_facility_use_plan'}_${index}`"
                >
                  {{ error }}
                </span>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <table class="table table-striped table-bordered">
            <thead>
              <tr>
                <th><strong>Key Metric</strong></th>
                <th><strong>Last season's performance</strong></th>
                <th><strong>Industry Target</strong></th>
                <th><strong>Our Goal</strong></th>
                <th><strong>By when</strong></th>
                <th><strong>Plan to Achieve Goal</strong></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Mortality Rate Cows</strong></td>
                <td>{{ percentFor('mortality_cows_performance') }}</td>
                <td>{{ farmData.mortality_cows_target }}</td>
                <td>{{ percentFor('mortality_cows_goal') }}</td>
                <td>{{ farmData.mortality_cows_by_when }}</td>
                <td>{{ farmData.mortality_cows_plan }}</td>
              </tr>
              <tr>
                <td><strong>Mortality Rate calves (pre weaning)</strong></td>
                <td>{{ percentFor('mortality_calf_performance') }}</td>
                <td>{{ farmData.mortality_calf_target }}</td>
                <td>{{ percentFor('mortality_calf_goal') }}</td>
                <td>{{ farmData.mortality_calf_by_when }}</td>
                <td>{{ farmData.mortality_calf_plan }}</td>
              </tr>
              <tr>
                <td><strong>BCS</strong></td>
                <td>{{ farmData.bcs_performance }}</td>
                <td>{{ farmData.bcs_target }}</td>
                <td>{{ farmData.bcs_goal }}</td>
                <td>{{ farmData.bcs_by_when }}</td>
                <td>{{ farmData.bcs_plan }}</td>
              </tr>
              <tr>
                <td><strong>Cows with BCS &lt; 3.5</strong></td>
                <td>{{ percentFor('bcs_lt35_performance') }}</td>
                <td>{{ farmData.bcs_lt35_target }}</td>
                <td>{{ percentFor('bcs_lt35_goal') }}</td>
                <td>{{ farmData.bcs_lt35_by_when }}</td>
                <td>{{ farmData.bcs_lt35_plan }}</td>
              </tr>
              <tr>
                <td><strong>Clinical mastitis rate</strong></td>
                <td>{{ percentFor('mastitis_performance') }}</td>
                <td>{{ farmData.mastitis_target }}</td>
                <td>{{ percentFor('mastitis_goal') }}</td>
                <td>{{ farmData.mastitis_by_when }}</td>
                <td>{{ farmData.mastitis_plan }}</td>
              </tr>
              <tr>
                <td><strong>Lameness incidence</strong></td>
                <td>{{ percentFor('lameness_performance') }}</td>
                <td>{{ farmData.lameness_target }}</td>
                <td>{{ percentFor('lameness_goal') }}</td>
                <td>{{ farmData.lameness_by_when }}</td>
                <td>{{ farmData.lameness_plan }}</td>
              </tr>
              <tr>
                <td><strong>AM usage (Annual PCU)</strong></td>
                <td>{{ farmData.amu_performance ? `${farmData.amu_performance}mg/kg` : '' }}</td>
                <td>{{ farmData.amu_target }}</td>
                <td>{{ farmData.amu_goal ? `${farmData.amu_goal}mg/kg` : '' }}</td>
                <td>{{ farmData.amu_by_when }}</td>
                <td>{{ farmData.amu_plan }}</td>
              </tr>
							<tr>
								<td colspan='6' class='text-left'>
									<strong>Reproduction</strong>
								</td>
							</tr>
              <tr>
                <td><strong>3 week ICR</strong></td>
                <td>{{ percentFor('three_weeks_performance') }}</td>
                <td>{{ farmData.three_weeks_target }}</td>
								<td>{{ percentFor('three_weeks_goal') }}</td>
                <td>{{ farmData.three_weeks_by_when }}</td>
                <td rowspan='3'>{{ farmData.icr_plan }}</td>
              </tr>
              <tr>
                <td><strong>6 week ICR</strong></td>
                <td>{{ percentFor('six_weeks_performance') }}</td>
                <td>{{ farmData.six_weeks_target }}</td>
								<td>{{ percentFor('six_weeks_goal') }}</td>
                <td>{{ farmData.six_weeks_by_when }}</td>
              </tr>
              <tr>
                <td><strong>NICR</strong></td>
                <td>{{ percentFor('nine_weeks_performance') }}</td>
                <td>{{ farmData.nine_weeks_target }}</td>
								<td>{{ percentFor('nine_weeks_goal') }}</td>
                <td>{{ farmData.nine_weeks_by_when }}</td>
              </tr>

            </tbody>
          </table>

          <table class="table table-striped table-bordered">
            <thead>
              <tr>
                <th colspan="2"><strong>Calf Plans</strong></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="text-left"><strong>Calf Nutrition Plan</strong></td>
                <td>{{ farmData.calf_nutrition_plan }}</td>
              </tr>
              <tr>
                <td class="text-left"><strong>Calf Health Plan</strong></td>
                <td>{{ farmData.calf_health_plan }}</td>
              </tr>
              <tr>
                <td class="text-left"><strong>Calf Housing Plan</strong></td>
                <td>{{ farmData.calf_housing_plan }}</td>
              </tr>
              <tr>
                <td class="text-left"><strong>Youngstock Plan - weaning to pregnancy</strong></td>
                <td>{{ farmData.weaning_plan }}</td>
              </tr>
            </tbody>
          </table>

          <table class="table table-striped table-bordered">
            <thead>
              <tr>
                <th colspan="2"><strong>Farm Plans</strong></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="text-left"><strong>Adverse Weather Plan</strong></td>
                <td>{{ farmData.adverse_weather_plan }}</td>
              </tr>
              <tr>
                <td class="text-left"><strong>Heat Stress Plan</strong></td>
                <td>{{ farmData.heat_stress_plan }}</td>
              </tr>
              <tr>
                <td class="text-left"><strong>Genetic Improvement (AW) Plan</strong></td>
                <td>{{ farmData.genetic_improvement_plan }}</td>
              </tr>

              <tr>
                <td class="text-left"><strong>Off-Pasture Grazing Facility Use Plan</strong></td>
                <td>{{ farmData.off_pasture_grazing_facility_use_plan }}</td>
              </tr>
            </tbody>
          </table>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import { mapState, mapActions } from 'vuex'
import { reportService, BACKEND_URL } from '@/_services'
import { get_dairy_season_start_year } from '@/_helpers/utils'

export default {
  name: 'FonterraReportPage',
  components: {
    vSelect,
  },
  data() {
    return {
      loaded: false,
      farmData: null,
      farmErrors: null,
      formEnabled: false,
      originalData: null,
      showServerError: false,
      selected: {
        id: null,
        farm_name: '',
      },
    }
  },
  mounted() {
    if (!this.currentClient) {
      this.getClient(this.$route.params.client_id)
    }
  },
  computed: {
    ...mapState('client', ['currentClient']),
    ...mapState('account', ['user']),
    canSave: function () {
      return this.formEnabled && !this.previousReportDataPresent()
    },
    canEdit: function () {
      return !this.formEnabled
      // return ['initial', 'saving'].includes(this.farmData.state)
    },
    farms: function () {
      return this.currentClient ? this.currentClient.fonterra_farms : []
    },
    exportUrl: function () {
      return `${BACKEND_URL}/reports/${this.currentClient.id}/fonterra/${this.selected.id}/season/${this.farmData.season_start_year}/export_pdf`
    },
    hasData: function () {
      return this.loaded && this.farmData != null
    },
    canDownloadPdf: function () {
      if (this.formEnabled) {
        return false
      }
      return this.farmData.can_download_pdf
    },
  },
  methods: {
    ...mapActions('client', ['getClient']),
    previousReportDataPresent: function () {
      return Object.values(this.farmData).some((value) => (String(value) || '').startsWith('(previous report)'))
    },
    removePreviousText(fieldName) {
      if ((this.farmData[fieldName] || '').startsWith('(previous report)')) {
        this.farmData[fieldName] = this.farmData[fieldName].replace('(previous report) ', '')
      }
    },
    percentFor: function (field) {
      return this.farmData[field] ? `${this.farmData[field]}%` : ''
    },
    get_dairy_season_start_year: get_dairy_season_start_year,
    errorsFor: function (fieldName) {
      return this.errors.collect(fieldName)
    },
    storeData: function () {
      this.errors.clear()
      this.farmErrors = null
      reportService
        .storeFonterraReportData(
          this.$route.params.client_id,
          this.selected.id,
          this.farmData.season_start_year,
          this.farmData,
        )
        .then((farmData) => {
          this.farmData = Object.assign({}, farmData)
          this.originalData = Object.assign({}, farmData)
          this.errors.clear()
          this.formEnabled = false
        })
        .catch((error) => {
          if (error.response.data['consult_already_finished']) {
            this.showFinishedError = true
          }
          if (error.response.data['errors']) {
            this.farmErrors = error.response.data['errors']
            for (let [fieldName, fieldErrors] of Object.entries(this.farmErrors)) {
              fieldErrors.forEach((errorMsg) => {
                this.errors.add({ field: fieldName, msg: errorMsg })
              })
            }
          }
        })
    },
    enableForm: function () {
      this.formEnabled = true
      this.showServerError = false
    },
    discardAndDisableForm: function () {
      this.formEnabled = false
      this.farmData = Object.assign({}, this.originalData)
      this.showServerError = false
    },
    resetFarm: function () {
      this.selected = { id: null, farm_name: '' }
      this.loaded = false
      this.farmData = null
      this.originalData = null
      this.showServerError = false
    },
    fetchFarmData: function () {
      let self = this
      reportService
        .getFonterraReportData(this.currentClient.id, this.selected.id, get_dairy_season_start_year())
        .then(function (farmData) {
          self.farmData = Object.assign({}, farmData)
          self.originalData = Object.assign({}, farmData)
          self.loaded = true
          self.showServerError = false
        })
        .catch(function () {
          self.showServerError = true
        })
    },
  },
}
</script>

<style>
.metric-form-box {
  border: 1px solid rgba(0, 0, 0, 0.3);
  border-radius: 5px;
  padding: 10px;
  margin-bottom: 10px;
}
</style>
